/**
 * Master template content context.
 *
 * Composes public `/website/:username` bootstrap (profile + template settings),
 * live operational data, and feed controls for the read-only site shell.
 *
 * Data sources:
 *   fetchWebsiteTemplateBootstrap → user_id + template_settings (one fast query)
 *   useCreatorDataFromDB          → CreatorData-shaped liveData + hasData
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useQuery } from "../../../Utils/query";
import { queryClient } from "../../../Utils/query/queryClient";
import { fetchFollowerControls } from "../../../Utils/SupabaseServices/ControlsDatabase";
import { supabasePublic } from "../../../config/supabaseClient";
import type { CreatorData, Tweaks } from "./types";
import type { HasData } from "./useCreatorDataFromDB";
import { useCreatorDataFromDB } from "./useCreatorDataFromDB";
import { FALLBACK_TEMPLATE_TWEAKS, expandTweaksFromStylePreset } from "./templateSettingsMap";
import {
  fetchWebsiteTemplateBootstrap,
  websiteTemplateBootstrapQueryKey,
} from "./websiteTemplateBootstrap";
import {
  parsePublicWebsiteSubpath,
} from "./websitePublicTemplatePath";
import { isContentPlatformBrowseSlug } from "./websiteEditorInspectorTypes";

import {
  useLiveWebsiteLoaderTweaks,
  writeCachedWebsiteLoaderTweaks,
} from "./websiteTemplateTweaksCache";
import { getCreatorSpaRelativePath } from "../../../Utils/url/getCreatorSpaRelativePath";
import { ensurePublicTemplateFonts } from "./ensurePublicTemplateFonts";
import { resolveTheme } from "./resolveTheme";

/** Extract public website subpath after /website/:user or /:username. */
function publicWebsiteSubpath(pathname: string, username?: string): string | null {
  const websiteMatch = pathname.match(/^\/website\/[^/]+\/?(.*)$/i);
  if (websiteMatch) {
    return websiteMatch[1] || "";
  }
  if (username) {
    const escaped = username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return pathname.replace(new RegExp(`^/${escaped}/?`, "i"), "");
  }
  return null;
}

/** True for /shop/{collection} or /content/{series|collection} deep-links. */
function isCollectionDeepLinkPath(pathname: string, username?: string): boolean {
  const rest = publicWebsiteSubpath(pathname, username);
  if (rest == null) return false;
  const parsed = parsePublicWebsiteSubpath(rest);
  if (parsed.productCollectionId) return true;
  const dl = parsed.carouselDeepLink;
  if (!dl?.slug) return false;
  if (dl.shopBrowse?.primarySlug) return false;
  if (
    dl.shopView === "category" ||
    dl.shopView === "brand" ||
    dl.shopView === "trending"
  ) {
    return false;
  }
  if (dl.target === "shop" && dl.shopView === "all") return true;
  if (dl.target === "content" && dl.contentView === "series") return true;
  if (
    dl.target === "content" &&
    dl.slug &&
    !isContentPlatformBrowseSlug(dl.slug)
  ) {
    return true;
  }
  return false;
}

/**
 * Defer StayUpToDate + Phase 2 pools on public website pages so Bio / nav /
 * featured-strip RPCs own the first network window on Slow 4G / 3G.
 * Link-in-Bio needs stayuptodate immediately — never defer that page.
 * Idle warm in useCreatorDataFromDB still fills deferred pools afterward.
 */
function shouldDeferHeavyPoolsForPublicPath(
  pathname: string,
  username?: string,
): boolean {
  if (isCollectionDeepLinkPath(pathname, username)) return true;
  const rest = publicWebsiteSubpath(pathname, username);
  if (rest == null) return false;
  const pageKey = parsePublicWebsiteSubpath(rest).pageKey;
  if (pageKey === "linkInBio") return false;
  // home, shop, content, about, contact, search, …
  return true;
}
// ── Merged surface (SquareMergedContent–style) ─────────────────
export type MasterTemplateDesignTokens = {
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  fontFamily?: string;
};
export { websiteTemplateBootstrapQueryKey };


export type MasterTemplateMergedContent = {
  /** Public handle from route / query / prop */
  username: string | undefined;
  userId: string | undefined;
  /** Prefix for rewriting internal links (e.g. `/website/alice`) */
  basePath: string;
  /** Live creator payload for sections (bio, links, carousels, …) */
  liveData: Partial<CreatorData>;
  hasData: HasData;
  feedControls: Record<string, unknown> | undefined;
  showSocials: unknown[] | undefined;
  /** Always undefined — website template uses `template_settings` / `ThemeTokens`, not LiB design. */
  designTokens: undefined;
  isProfileLoading: boolean;
  isLiveLoading: boolean;
  /** Blog fetch still in flight after home-capable live data has painted. */
  isJournalLoading: boolean;
  /** `template_settings` row — loaded with profile in one bootstrap query. */
  isSettingsLoading: boolean;
  preloadedTemplateSettings?: {
    tweaks: Tweaks;
    creatorData: Partial<CreatorData>;
  };
  /** Saved `template_settings.tweaks` — use for bootstrap loader theming. */
  templateTweaks: Tweaks;
  /** Tweaks resolved for the public loader (live bootstrap or session cache). */
  loaderThemeTweaks: Tweaks;
  /** True when loader can use saved/cached preset tokens (not neutral pending chrome). */
  loaderThemeReady: boolean;
  /** True when profile query completed and user does not exist */
  userNotFound: boolean;
  /** True when the creator has never published — public `/website` is gated. */
  websiteUnpublished: boolean;
  /**
   * Published website AND full Website (can Publish). False for Demo-only even
   * if a previous publish stamp remains.
   */
  hasWebsiteSurface: boolean;
  /** Dashboard `hasPages` — Website / Website Demo / growth onboarding fallback. */
  hasWebsiteProductAccess: boolean;
  /** Full Website go-live entitlement. */
  canPublishWebsite: boolean;
  /** Admin `website_demo` — only true when full Website cannot Publish. */
  hasWebsiteDemo: boolean;
  /** `/website/:username` may serve the draft/live website shell. */
  canServeWebsiteDemoPath: boolean;
  /** True when profile query failed (network / server — not “no user”) */
  profileError: boolean;
};

const MasterTemplateContentContext =
  createContext<MasterTemplateMergedContent | null>(null);

export interface MasterTemplateContentProviderProps {
  children: React.ReactNode;
  /**
   * When set, overrides route + `?username=` resolution (e.g. embeds / tests).
   */
  usernameOverride?: string;
  /**
   * Treat as public website even when the browser path is canonical `/{username}`
   * (no `/website` prefix).
   */
  forcePublicWebsite?: boolean;
}

export function MasterTemplateContentProvider({
  children,
  usernameOverride,
  forcePublicWebsite = false,
}: MasterTemplateContentProviderProps) {
  const param = useParams<{ username?: string }>();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const username = useMemo(() => {
    const raw =
      (usernameOverride ?? "").trim() ||
      searchParams.get("username")?.trim() ||
      param.username?.trim() ||
      "";
    if (!raw) return undefined;
    try {
      return decodeURIComponent(raw).toLowerCase();
    } catch {
      return raw.toLowerCase();
    }
  }, [usernameOverride, searchParams, param.username]);

  const isPublicWebsite =
    forcePublicWebsite || 
    pathname.startsWith("/website") ||
    /(?:^|\/)shop\/collections\//i.test(pathname);

  const basePath = useMemo(() => {
    if (!username) return "/";
    return getCreatorSpaRelativePath({
      username,
      pathname: forcePublicWebsite ? "/" : pathname,
    });
  }, [pathname, username, forcePublicWebsite]);

  const bootstrapQuery = useQuery(
    websiteTemplateBootstrapQueryKey(username ?? ""),
    ({ signal }) => fetchWebsiteTemplateBootstrap(username!, signal),
    {
      enabled: Boolean(username),
      // Short stale window: avoid cancelling an in-flight prefetch on every
      // mount (that race randomly aborted bootstrap → permanent skeletons).
      // Publish still clears cache via removeQueries / loader LS wipe.
      staleTime: 30_000,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const bootstrap = bootstrapQuery.data;

  // Only treat bootstrap as authoritative for the loader after this mount's
  // refetch settles — otherwise a stale per-tab RQ row can overwrite the
  // Publish-updated localStorage palette (and paint the wrong @handle theme).
  const hasFreshBootstrap = Boolean(
    bootstrap &&
      !bootstrap.userNotFound &&
      bootstrapQuery.isFetchedAfterMount &&
      !bootstrapQuery.isFetching,
  );

  const [loaderCacheTick, setLoaderCacheTick] = useState(0);
  const liveLoaderTweaks = useLiveWebsiteLoaderTweaks(username);

  useEffect(() => {
    if (!username || !hasFreshBootstrap || !bootstrap || bootstrap.userNotFound) {
      return;
    }
    writeCachedWebsiteLoaderTweaks(username, bootstrap.tweaks);
    setLoaderCacheTick((n) => n + 1);
  }, [username, hasFreshBootstrap, bootstrap]);

  useEffect(() => {
    if (!isPublicWebsite || !bootstrap || bootstrap.userNotFound) return;
    const theme = resolveTheme(expandTweaksFromStylePreset(bootstrap.tweaks));
    ensurePublicTemplateFonts(theme.display, theme.body);
  }, [isPublicWebsite, bootstrap]);

  useEffect(() => {
    if (!username || typeof window === "undefined") return;
    const cacheKey = `website_tpl_loader_tweaks_v1:${username.trim().toLowerCase()}`;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== cacheKey) return;
      // Publish cleared the cache in another tab — drop stale RQ so we don't
      // paint the previous palette while the fresh theme loads.
      if (e.newValue == null) {
        try {
          queryClient.removeQueries(
            websiteTemplateBootstrapQueryKey(username.trim().toLowerCase()),
          );
        } catch {
          /* ignore */
        }
      }
      setLoaderCacheTick((n) => n + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [username]);

  const userNotFound = Boolean(bootstrap?.userNotFound);
  const websiteUnpublished = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.unpublished,
  );
  const hasWebsiteSurface = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.hasWebsiteSurface,
  );
  const hasWebsiteProductAccess = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.hasWebsiteProductAccess,
  );
  const canPublishWebsite = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.canPublishWebsite,
  );
  const hasWebsiteDemo = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.hasWebsiteDemo,
  );
  const canServeWebsiteDemoPath = Boolean(
    bootstrap && !bootstrap.userNotFound && bootstrap.canServeWebsiteDemoPath,
  );
  const profileError = Boolean(bootstrapQuery.isError);
  const userId =
    bootstrap && !bootstrap.userNotFound ? bootstrap.userId : undefined;

  const templateTweaksForEditor =
    bootstrap && !bootstrap.userNotFound
      ? expandTweaksFromStylePreset(bootstrap.tweaks)
      : FALLBACK_TEMPLATE_TWEAKS;

  const preloadedTemplateSettings = useMemo(
    () =>
      bootstrap && !bootstrap.userNotFound
        ? {
            tweaks: expandTweaksFromStylePreset(bootstrap.tweaks),
            creatorData: bootstrap.creatorData,
          }
        : undefined,
    [bootstrap],
  );

  const loaderThemeTweaks = useMemo(() => {
    // Loader colors come from this visit's database read, never localStorage.
    if (liveLoaderTweaks) return liveLoaderTweaks;
    // Cached / in-flight-complete bootstrap is enough to theme — do not wait
    // for isFetchedAfterMount (that forced an extra Slow 4G RTT after prefetch).
    if (bootstrap && !bootstrap.userNotFound) {
      return expandTweaksFromStylePreset(bootstrap.tweaks);
    }
    return FALLBACK_TEMPLATE_TWEAKS;
  }, [bootstrap, liveLoaderTweaks]);

  const loaderThemeReady = Boolean(
    liveLoaderTweaks || (bootstrap && !bootstrap.userNotFound),
  );

  // Unlock as soon as we have userId from cache/prefetch. Background refetch
  // must not keep the blank public loader up for another full bootstrap RTT.
  const isBootstrapLoading = Boolean(
    username?.trim() &&
      !userNotFound &&
      !profileError &&
      !userId?.trim(),
  );

  const feedControlsQuery = useQuery(
    ["websiteFeedControls", username ?? ""],
    async () => {
      const controls = await fetchFollowerControls(username!, supabasePublic);
      if (
        controls != null &&
        typeof controls === "object" &&
        !Array.isArray(controls)
      ) {
        return controls as Record<string, unknown>;
      }
      return {};
    },
    {
      // Wait for bootstrap userId so feed-controls does not steal the first
      // Slow 4G RTT from user → template_settings / template_components.
      enabled: isPublicWebsite && Boolean(username) && Boolean(userId?.trim()),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    liveData,
    hasData,
    loading: isLiveLoading,
    journalLoading: isJournalLoading,
  } = useCreatorDataFromDB(userId, username, {
    deferHeavyPools: Boolean(
      username && shouldDeferHeavyPoolsForPublicPath(pathname, username),
    ),
  });

  const merged = useMemo<MasterTemplateMergedContent>(() => {
    return {
      username,
      userId,
      basePath,
      liveData,
      hasData,
      feedControls: feedControlsQuery.data ?? {},
      showSocials: undefined,
      designTokens: undefined,
      isProfileLoading: isBootstrapLoading,
      isLiveLoading: Boolean(userId && isLiveLoading),
      isJournalLoading: Boolean(userId && isJournalLoading),
      isSettingsLoading: isBootstrapLoading,
      preloadedTemplateSettings,
      templateTweaks: templateTweaksForEditor,
      loaderThemeTweaks,
      loaderThemeReady,
      userNotFound,
      websiteUnpublished,
      hasWebsiteSurface,
      hasWebsiteProductAccess,
      canPublishWebsite,
      hasWebsiteDemo,
      canServeWebsiteDemoPath,
      profileError,
    };
  }, [
    username,
    userId,
    basePath,
    liveData,
    hasData,
    feedControlsQuery.data,
    isBootstrapLoading,
    isLiveLoading,
    isJournalLoading,
    preloadedTemplateSettings,
    templateTweaksForEditor,
    loaderThemeTweaks,
    loaderThemeReady,
    userNotFound,
    websiteUnpublished,
    hasWebsiteSurface,
    hasWebsiteProductAccess,
    canPublishWebsite,
    hasWebsiteDemo,
    canServeWebsiteDemoPath,
    profileError,
  ]);

  return (
    <MasterTemplateContentContext.Provider value={merged}>
      {children}
    </MasterTemplateContentContext.Provider>
  );
}

/** Full merged template content (requires provider). */
export function useMasterTemplateContent(): MasterTemplateMergedContent {
  const ctx = useContext(MasterTemplateContentContext);
  if (!ctx) {
    throw new Error(
      "useMasterTemplateContent must be used within MasterTemplateContentProvider",
    );
  }
  return ctx;
}

/** Optional hook for components that may render outside the provider. */
export function useMasterTemplateContentOptional(): MasterTemplateMergedContent | null {
  return useContext(MasterTemplateContentContext);
}
